import 'immer';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import type {
  PackingSlip,
  PackingSlipDeleteInput,
  PackingSlipSearchOutput,
  PackingSlipSearchInput,
} from '@repo/api-contract';
import ApiClient from '../../util/api-client';
type SearchBy = PackingSlipSearchInput['searchBy'];

export interface PackingSlipBrowseState {
  searchInput: string;
  results?: PackingSlipSearchOutput;
  searchBy: SearchBy;
  loading: boolean;
  activeSelection?: PackingSlip;
}

// Initialize state
const initialState: PackingSlipBrowseState = {
  searchInput: '',
  results: undefined,
  searchBy: 'packingSlipCode',
  loading: false,
  activeSelection: undefined,
};

export const packingSlipBrowseSlice = createSlice({
  name: 'packingSlipBrowse',
  initialState,
  reducers: {
    setSearchInput: (state, action: PayloadAction<string>) => ({
      ...state,
      searchInput: action.payload,
    }),
    setSearchBy: (state, action: PayloadAction<SearchBy>) => ({
      ...state,
      searchBy: action.payload,
    }),
    setResults: (
      state,
      action: PayloadAction<PackingSlipSearchOutput | undefined>,
    ) => ({
      ...state,
      results: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setActiveSelection: (state, action: PayloadAction<PackingSlip>) => ({
      ...state,
      activeSelection: action.payload,
    }),
  },
});

export const {
  setSearchInput,
  setSearchBy,
  setResults,
  setLoading,
  setActiveSelection,
} = packingSlipBrowseSlice.actions;
export default packingSlipBrowseSlice.reducer;

// Thunks
export const deletePackingSlip =
  (deleteInput: PackingSlipDeleteInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setLoading(true));
      await ApiClient.deletePackingSlip.mutate(deleteInput);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const searchPackingSlips =
  (searchInput: PackingSlipSearchInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const results = await ApiClient.searchPackingSlips.query(searchInput);
      dispatch(setResults(results));
    } catch (error: unknown) {
      dispatch(setResults(undefined));
    } finally {
      dispatch(setLoading(false));
    }
  };
